import { Component, computed, inject, signal } from '@angular/core';
import { LvrCommonTemplateEditor } from './common-template-editor';
import { LvrRedirectNav } from '../../../../core/classes/redirect-nav.class';
import { ClientDocumentsResponse } from '../../../../api/app-types/document.app';
import { UPLOAD_API } from '../../../../api/services/upload.api';
import { TranslocoPipe } from '@jsverse/transloco';
import { toTemplateSlug } from '../../../../shared/helpers/to-template-slug.func';

@Component({
	selector: 'lvr-template-editor-view',
	standalone: true,
	imports: [LvrCommonTemplateEditor, TranslocoPipe],
	template: `
		@if (selectedTemplate(); as t) {
			<lvr-common-template-editor
				[template]="t"
				[templates]="templates()"
				[backLink]="backLink()"
				(htmlChanged)="onHtmlChanged($event)"
				(navigateToTemplate)="onNavigateToTemplate($event)"
				(printClicked)="onPrint()"
				[isPrinting]="isPrinting()"
				(exportClicked)="onExport()"
				[isProcessing]="isProcessing()"
			/>
		} @else {
			<p>{{ 'client.documents.no_templates' | transloco }}</p>
		}
	`,
})
export class LvrTemplateEditorView extends LvrRedirectNav {
	private readonly api = inject(UPLOAD_API);
	private readonly clientDocuments = this.resolveDataAsSignal<ClientDocumentsResponse>('clientDocuments');
	private readonly clientId = this.resolveRouteParamAsSignal('id');
	private readonly templateId = this.resolveRouteParamAsSignal('templateId');

	public readonly backLink = computed(() => `/desk/client/${this.clientId()}/documents`);
	public readonly templates = computed(() => this.clientDocuments()?.commonEntries ?? []);
	public readonly selectedTemplate = computed(() => {
		const templateId = this.templateId();
		return this.templates().find((t) => toTemplateSlug(t.name) === templateId) ?? null;
	});

	public readonly editedHtml = signal<string | null>(null);
	public readonly isPrinting = signal(false);
	public readonly isProcessing = signal(false);

	public onHtmlChanged(html: string) {
		this.editedHtml.set(html);
	}

	private getHtmlToExport(): string {
		return this.editedHtml() ?? this.selectedTemplate()?.htmlContent ?? '';
	}

	public onNavigateToTemplate(templateId: string) {
		this.navigateAfterFinished(['/desk/client', this.clientId(), 'documents', 'templates', templateId]);
	}

	public async onPrint() {
		if (this.isPrinting()) return;
		this.isPrinting.set(true);

		const html = this.getHtmlToExport();
		const fileName = `${this.selectedTemplate()?.name.split('.')[0] ?? 'template'}.pdf`;
		const blob = await this.api.pdfFromHtml({ htmlContent: html, fileName });

		this.openPdfAndPrint(blob);

		this.isPrinting.set(false);
	}

	public async onExport() {
		if (this.isProcessing()) return;
		this.isProcessing.set(true);

		try {
			const html = this.getHtmlToExport();
			const fileName = `${this.selectedTemplate()?.name.split('.')[0] ?? 'template'}.pdf`;
			const blob = await this.api.pdfFromHtml({ htmlContent: html, fileName });

			this.downloadBlob(blob, fileName);
		} finally {
			this.isProcessing.set(false);
		}
	}

	private downloadBlob(blob: Blob, fileName: string) {
		const url = URL.createObjectURL(blob);

		const a = document.createElement('a');
		a.href = url;
		a.download = fileName;
		a.click();

		URL.revokeObjectURL(url);
	}

	private openPdfAndPrint(blob: Blob) {
		const url = URL.createObjectURL(blob);
		const win = window.open(url, '_blank');
		if (!win) return;

		win.addEventListener('load', () => {
			win.focus();
			win.print();
		});

		setTimeout(() => URL.revokeObjectURL(url), 60_000);
	}
}
